import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Search, Sparkles, MapPin } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Restaurant } from '../types';
import { SafetyBadge } from './SafetyBadge';
interface SearchBarProps {
  initialQuery?: string;
  onSearch: (query: string) => void;
  onAskAI?: (query: string) => void;
  onSelectSuggestion?: (id: string) => void;
  suggestions?: Restaurant[];
  onQueryChange?: (query: string) => void;
  className?: string;
}
export function SearchBar({
  initialQuery = '',
  onSearch,
  onAskAI,
  onSelectSuggestion,
  suggestions = [],
  onQueryChange,
  className = ''
}: SearchBarProps) {
  const [query, setQuery] = useState(initialQuery);
  const [isFocused, setIsFocused] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const showDropdown = isFocused && query.length > 0 && suggestions.length > 0;
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    setHighlightedIndex(-1);
    onQueryChange?.(val);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
      handleSelect(suggestions[highlightedIndex].id);
      return;
    }
    if (query.trim()) {
      setIsFocused(false);
      onSearch(query.trim());
    }
  };
  const handleSelect = (id: string) => {
    setIsFocused(false);
    setQuery('');
    onQueryChange?.('');
    onSelectSuggestion?.(id);
  };
  const handleAskAI = () => {
    setIsFocused(false);
    onAskAI?.(query.trim());
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Escape') {
      setIsFocused(false);
    }
  };
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Highlight matching text in suggestion
  const highlightMatch = (text: string, q: string) => {
    if (!q) return text;
    const idx = text.toLowerCase().indexOf(q.toLowerCase());
    if (idx === -1) return text;
    return <>
        {text.slice(0, idx)}
        <span className="text-teal-700 font-bold" data-id="element-118">
          {text.slice(idx, idx + q.length)}
        </span>
        {text.slice(idx + q.length)}
      </>;
  };
  return <div ref={wrapperRef} className={`relative w-full max-w-2xl mx-auto ${className}`} data-id="element-119">
      <form onSubmit={handleSubmit} className="relative flex items-center w-full" data-id="element-120">
        <div className="relative flex-grow" data-id="element-121">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-122">
            <Search className="h-5 w-5 text-slate-400" data-id="element-123" />
          </div>
          <input ref={inputRef} type="text" value={query} onChange={handleChange} onFocus={() => setIsFocused(true)} onKeyDown={handleKeyDown} className={`block w-full pl-11 pr-28 py-3.5 bg-white border-0 text-slate-900 ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-teal-600 text-base shadow-sm transition-all hover:shadow-md ${showDropdown ? 'rounded-t-2xl rounded-b-none ring-teal-600 ring-2' : 'rounded-2xl'}`} placeholder="Search restaurants or ask a question..." role="combobox" aria-expanded={showDropdown} aria-autocomplete="list" aria-activedescendant={highlightedIndex >= 0 ? `suggestion-${highlightedIndex}` : undefined} data-id="element-124" />
          <div className="absolute inset-y-0 right-2 flex items-center gap-1" data-id="element-125">
            {onAskAI && <button type="button" onClick={handleAskAI} className="flex items-center gap-1.5 px-3 py-1.5 text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-xl transition-colors text-sm font-semibold" aria-label="Ask AI" data-id="element-126">
                <Sparkles className="h-4 w-4" data-id="element-127" />
                <span className="hidden sm:inline" data-id="element-128">Ask AI</span>
              </button>}
          </div>
        </div>
        <button type="submit" className="hidden sm:block ml-3 bg-teal-600 text-white px-5 py-3.5 rounded-2xl font-semibold shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 transition-colors text-sm" data-id="element-129">
          Search
        </button>
      </form>

      {/* Typeahead Dropdown */}
      <AnimatePresence data-id="element-130">
        {showDropdown && <motion.div initial={{
        opacity: 0,
        y: -4
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -4
      }} transition={{
        duration: 0.15
      }} className="absolute top-full left-0 right-0 sm:right-auto sm:w-[calc(100%-76px)] bg-white rounded-b-2xl shadow-lg ring-2 ring-teal-600 ring-t-0 z-50 overflow-hidden max-h-80 overflow-y-auto" role="listbox" data-id="element-131">
            <div className="px-3 py-2" data-id="element-132">
              <p className="text-xs font-medium text-slate-400 uppercase tracking-wider px-2" data-id="element-133">
                {suggestions.length} match{suggestions.length !== 1 ? 'es' : ''}
              </p>
            </div>
            {suggestions.slice(0, 6).map((restaurant, idx) => {
          const latestViolations = restaurant.inspections[0]?.violations.length || 0;
          return <button key={restaurant.id} id={`suggestion-${idx}`} role="option" aria-selected={highlightedIndex === idx} onClick={() => handleSelect(restaurant.id)} onMouseEnter={() => setHighlightedIndex(idx)} className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${highlightedIndex === idx ? 'bg-teal-50' : 'hover:bg-slate-50'}`} data-id="element-134">
                  <SafetyBadge grade={restaurant.currentGrade} data-id="element-135" />
                  <div className="flex-1 min-w-0" data-id="element-136">
                    <p className="text-sm font-semibold text-slate-900 truncate" data-id="element-137">
                      {highlightMatch(restaurant.name, query)}
                    </p>
                    <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5" data-id="element-138">
                      <span data-id="element-139">{restaurant.cuisine}</span>
                      <span className="text-slate-300" data-id="element-140">·</span>
                      <MapPin className="w-3 h-3 inline" data-id="element-141" />
                      <span className="truncate" data-id="element-142">{restaurant.address}</span>
                    </p>
                  </div>
                  {latestViolations > 0 && <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-md font-medium shrink-0" data-id="element-143">
                      {latestViolations} issue
                      {latestViolations !== 1 ? 's' : ''}
                    </span>}
                </button>;
        })}
            {suggestions.length > 6 && <div className="px-4 py-2.5 border-t border-slate-100" data-id="element-144">
                <button onClick={() => {
            setIsFocused(false);
            onSearch(query.trim());
          }} className="text-sm text-teal-600 font-medium hover:text-teal-700" data-id="element-145">
                  See all {suggestions.length} results →
                </button>
              </div>}
          </motion.div>}
      </AnimatePresence>
    </div>;
}